import { validateImportCustomerUsersApi } from "@/api/services/main/customer-user";
import { importCustomerUsersStream } from "@/api/services/main/customer-user/import-customer-users-stream";
import { IconCloseFile } from "@/assets/icons/IconCloseFile";
import { IconDownloadTemplate } from "@/assets/icons/IconDownloadTemplate";
import { IconFile } from "@/assets/icons/IconFile";
import { IconImportFile } from "@/assets/icons/IconImportFile";
import { Button } from "@/components/elements/Button";
import { IconBox } from "@/components/elements/IconBox";
import { Modal } from "@/components/elements/Modal";
import { Progress } from "@/components/elements/Progress";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { ImportFileDropZone } from "@/features/customer-user/components/import/ImportFileDropZone";
import { useImportProductOptions } from "@/features/customer-user/hooks/useImportProductOptions";
import { useSelectableProductContracts } from "@/features/customer-user/hooks/useSelectableProductContracts";
import {
  collectMissingProductErrors,
  ImportParseError,
  parseImportFile,
  toImportRequestRow,
  TParsedImportRow,
} from "@/features/customer-user/utils/importParse";
import { downloadCsvTemplate, downloadExcelTemplate } from "@/features/customer-user/utils/importTemplate";
import { useDialog } from "@/hooks/useDialog";
import { background, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";

const MAX_IMPORT_ROWS = 200;
const FILE_CHIP_COLOR = "#185DE3";

type TCustomerUserImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CustomerUserImportModal: FC<TCustomerUserImportModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const productOptions = useImportProductOptions();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<TParsedImportRow[]>([]);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const confirmDialog = useDialog();
  // With no selectable contract there is nothing to grant, so the product column stays optional —
  // same gate the create/edit form uses.
  const { hasSelectableProductContracts } = useSelectableProductContracts();

  const resetState = () => {
    setFile(null);
    setRows([]);
    setErrorList([]);
    setProgress(0);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSelectFile = async (selected: File) => {
    setFile(selected);
    setRows([]);
    setErrorList([]);
    setIsProcessing(true);
    try {
      const parsed = await parseImportFile(selected);
      if (parsed.length === 0) {
        setErrorList(["ファイルにデータがありません。"]);
        return;
      }
      if (parsed.length > MAX_IMPORT_ROWS) {
        setErrorList([`一度に取り込めるユーザーは${MAX_IMPORT_ROWS}件までです。（現在: ${parsed.length}件）`]);
        return;
      }
      setRows(parsed);
    } catch (error) {
      if (error instanceof ImportParseError) {
        setErrorList([error.message]);
      } else {
        showError(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    setIsProcessing(true);
    setErrorList([]);
    try {
      const missingProductErrors = hasSelectableProductContracts ? collectMissingProductErrors(rows) : [];
      if (missingProductErrors.length > 0) {
        setErrorList(missingProductErrors);
        return;
      }

      const requestRows = rows.map(toImportRequestRow);
      const validateRes = await validateImportCustomerUsersApi(requestRows);
      if (validateRes.data.hasError) {
        setErrorList(validateRes.data.rows.flatMap((row) => row.errors));
        return;
      }

      confirmDialog.show();
    } catch (error) {
      showError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmImport = async () => {
    setIsProcessing(true);
    setProgress(0);
    let succeeded = false;
    let errorMessages: string[] | undefined;
    try {
      await importCustomerUsersStream(rows.map(toImportRequestRow), (chunk) => {
        if (chunk.progress === -1) {
          errorMessages = [chunk.message ?? "不明なエラー。"];
        } else if (chunk.successCount !== undefined) {
          setProgress(100);
          succeeded = true;
        } else {
          setProgress(chunk.progress);
        }
      });
    } catch (error) {
      showError(error);
    } finally {
      setIsProcessing(false);
    }

    if (succeeded) {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_LIST] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_USER.FETCH_COUNT] });
      toast.success("ユーザーを取り込みました。");
      confirmDialog.confirm();
      handleClose();
      return;
    }
    confirmDialog.cancel();
    if (errorMessages?.length) setErrorList(errorMessages);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} enableFooter={false} width="min(920px, 95vw)" title="データインポート">
        <Stack gap="16px" mt="8px">
          <Typography variant="body14" color={text.secondary}>
            ファイルからユーザーを一括で登録できます。
          </Typography>

          <ImportFileDropZone disabled={isProcessing} onSelectFile={handleSelectFile} />

          {file && (
            <Stack direction="row" alignItems="center" gap="8px">
              <IconFile color={FILE_CHIP_COLOR} fontSize={20} />
              <Typography variant="body14" color={FILE_CHIP_COLOR}>
                {file.name}
              </Typography>
              <IconBox onClick={resetState} sx={{ p: 0 }}>
                <IconCloseFile />
              </IconBox>
            </Stack>
          )}

          {errorList.length > 0 && (
            <Stack maxHeight="30vh" overflow="auto" gap="4px" sx={{ backgroundColor: background.error, borderRadius: rounded.sm, p: "16px" }}>
              {errorList.map((error, index) => (
                <Typography key={`${index}-${error}`} variant="body14" color={text.error}>
                  {error}
                </Typography>
              ))}
            </Stack>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt="8px">
            <Button variant="ghost" size="sm" onClick={handleClose} disabled={isProcessing}>
              キャンセル
            </Button>
            <Stack direction="row" gap="12px" alignItems="center">
              <Button variant="outline" size="sm" onClick={() => downloadCsvTemplate(productOptions)} startIcon={<IconDownloadTemplate />}>
                CSVテンプレート
              </Button>
              <Button variant="outline" size="sm" onClick={() => void downloadExcelTemplate(productOptions)} startIcon={<IconDownloadTemplate />}>
                Excelテンプレート
              </Button>
              <Button
                size="sm"
                onClick={handleImport}
                disabled={rows.length === 0}
                isLoading={isProcessing && !confirmDialog.open}
                startIcon={<IconImportFile />}
              >
                インポート
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Modal>

      <Modal
        isOpen={confirmDialog.open}
        onClose={confirmDialog.cancel}
        onOk={handleConfirmImport}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        isLoadingOkBtn={isProcessing}
      >
        <Stack mb={2} gap="12px">
          <Typography>このファイルには{rows.length}行のデータがあります。インポートしてもよろしいですか？</Typography>
          {isProcessing && (
            <Stack gap="8px">
              <Progress value={progress} />
              <Typography variant="body14" color={text.secondary} textAlign="center">
                処理中... {progress}%
              </Typography>
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  );
};
